/**
 * Formatters that turn raw attachment + correlation results into the
 * pre-formatted text blocks that `generate-document` expects.
 *
 * These are extracted from src/pages/Index.tsx so they can be unit-tested
 * without rendering the page or hitting the network.
 *
 * Citation contract:
 *   - Insight ID:  `r_{shortRun}:{kind}:{fileIndex}:0`
 *   - Citation:    `{LABEL_PREFIX}-{fileIndex}.0`   (e.g. PHOTO-0.0)
 * The model uses these tokens to cite specific attachments inline.
 */

export type AttachmentResult = {
  tags?: string[];
  summary?: string;
  error?: string;
};

export type AttachmentKindToken = "photo" | "drawing" | "doc";
export type AttachmentLabelPrefix = "PHOTO" | "DRAWING" | "DOC";

/** Stable 4-char run prefix derived from the runId UUID. */
export function shortRunFromRunId(runId: string): string {
  return runId.replace(/-/g, "").substring(0, 4);
}

/**
 * Format one kind of attachment (photos / drawings / docs) into the bullet
 * list that ships in `attachmentContext.{photos,drawings,docs}`.
 * Returns `undefined` when nothing usable is present so the edge function
 * can skip the block entirely.
 */
export function formatAttachmentKind(args: {
  results: AttachmentResult[];
  names: string[];
  kindToken: AttachmentKindToken;
  labelPrefix: AttachmentLabelPrefix;
  fileIndexBase: number;
  shortRun: string;
}): string | undefined {
  const { results, names, kindToken, labelPrefix, fileIndexBase, shortRun } = args;
  const lines = results
    .map((r, i) => {
      if (r.error || (!r.summary && !r.tags?.length)) return null;
      const id = `r_${shortRun}:${kindToken}:${fileIndexBase + i}:0`;
      const cite = `${labelPrefix}-${fileIndexBase + i}.0`;
      const tagPart = r.tags?.length ? ` [${r.tags.join(", ")}]` : "";
      const summaryPart = r.summary ? ` — ${r.summary}` : "";
      return `  - ${cite} (${id}) ${names[i] ?? "unnamed"}${summaryPart}${tagPart}`;
    })
    .filter(Boolean) as string[];
  return lines.length ? lines.join("\n") : undefined;
}

export type CorrelationRaw = {
  correlations?: Array<{
    primary_insight_id: string;
    linked_insight_ids: string[];
    relationship: string;
    rationale: string;
  }>;
  newHazards?: Array<{
    label: string;
    detail?: string;
    source_insight_ids: string[];
    suggested_tags?: string[];
  }>;
  contradictions?: Array<{
    conflict_summary: string;
    prompt_insight_id: string;
    attachment_insight_id: string;
    recommendation: string;
  }>;
};

export type FormattedCorrelation = {
  evidenceLinks?: string;
  newHazards?: string;
  contradictions?: string;
};

/**
 * Convert correlate-insights output into the three text blocks
 * generate-document expects. Returns undefined when nothing to send.
 */
export function formatCorrelation(raw: CorrelationRaw | undefined): FormattedCorrelation | undefined {
  if (!raw) return undefined;
  return {
    evidenceLinks: raw.correlations?.length
      ? raw.correlations
          .map(
            (c) =>
              `  - ${c.primary_insight_id} ↔ ${c.linked_insight_ids.join(", ")} [${c.relationship}] — ${c.rationale}`,
          )
          .join("\n")
      : undefined,
    newHazards: raw.newHazards?.length
      ? raw.newHazards
          .map(
            (h) =>
              `  - ${h.label}${h.detail ? ` — ${h.detail}` : ""} (evidence: ${h.source_insight_ids.join(", ")})${h.suggested_tags?.length ? ` [${h.suggested_tags.join(", ")}]` : ""}`,
          )
          .join("\n")
      : undefined,
    contradictions: raw.contradictions?.length
      ? raw.contradictions
          .map(
            (c) =>
              `  - ${c.conflict_summary} (prompt: ${c.prompt_insight_id} vs attachment: ${c.attachment_insight_id}) → ${c.recommendation}`,
          )
          .join("\n")
      : undefined,
  };
}
